import { AsyncPipe, CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SessionDetailsComponent } from '../../../manage-users/components/session-details/session-details.component';
import { ClickOutsideDirective } from '../../../../shared/directives/click-outside/click-outside.directive';
import { PopupComponent } from '../../../../shared/components/popup/popup.component';
import { MenuComponentBase } from '../../../../shared/base-classes/menu-component-base';
import { ImageService } from '../../../../shared/services/image-service/image.service';
import { LoggedInUser } from '../../../../shared/models/logged-in-user';
import { AuthService } from '../../../auth/services/auth.service';
import { finalize, takeUntil } from 'rxjs';
import { CdfiProfileComponent } from '../cdfi-profile/cdfi-profile.component';
import { ActivatedRoute, Router } from '@angular/router';
import { DndEmail } from '../../../configs/dnd-list/models/dnd-email';
import { DndEmailService } from '../../../configs/dnd-list/services/dnd-email.service';
import { ProfileService } from '../../services/profile.service';
import { UserDetails } from '../../../../shared/models/user';
import { CommonService } from '../../../../shared/services/common/common.service';
import { ToastType } from '../../../../shared/interfaces/common';

@Component({
  selector: 'app-user-profile',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SessionDetailsComponent,
    AsyncPipe,
    ClickOutsideDirective,
    PopupComponent,
    CdfiProfileComponent,
  ], // Added AsyncPipe
  templateUrl: './user-profile.component.html',
  styleUrl: './user-profile.component.scss',
})
export class UserProfileComponent extends MenuComponentBase implements OnInit {
  authService = inject(AuthService);
  imageService = inject(ImageService);
  dndEmailService = inject(DndEmailService);
  profileService = inject(ProfileService);
  commonService = inject(CommonService);
  user: LoggedInUser | null = null;
  dndEmail: DndEmail | null = null;
  proPic: string | undefined;
  profileImageUrl$ = this.imageService.profileImageUrl$;
  connectionLogoUrl: string | null = null;
  isProfileDropdownOpen = false;
  activatedRoute = inject(ActivatedRoute);
  router = inject(Router);

  activeTab: 'my-profile' | 'cdfi-profile' = 'my-profile';
  hasProfileImage = false;

  showDeletePopup = false;
  loadingDnd = false;
  loaderDnd = true;
  showProfileEditPopup = false;
  readonly isSmeUser = this.authService.isSMEUser();
  readonly isDHUser = this.authService.isDHUser();
  userProfileDetails?: UserDetails;

  editFirstName = '';
  editLastName = '';
  username = '';
  isProfileLoading = false;
  isFirstnameErr = false;
  override ngOnInit(): void {
    super.ngOnInit();

    this.activatedRoute.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        if (params['tab'] === 'cdfi-profile') {
          this.setActiveTab('cdfi-profile');
        } else {
          this.setActiveTab('my-profile');
        }
      });

    this.user = this.authService.getLoggedInUserInfo();
    if (this.user?.name) {
      this.proPic = this.user.name.charAt(0).toUpperCase();
    }

    // Subscribe to profile image changes to track if user has an image
    this.profileImageUrl$.pipe(takeUntil(this.destroy$)).subscribe((url) => {
      this.hasProfileImage = !!url;
    });

    // Subscribe to connection logo image from the shared image service
    this.imageService.connectionImageUrl$
      .pipe(takeUntil(this.destroy$))
      .subscribe((url) => {
        this.connectionLogoUrl = url;
      });
    const loggedInUser = this.authService.getLoggedInUserInfo();
    this.username = loggedInUser?.username || '';
    this.getProfileDetails();
    this.loadDndEmail();
  }

  getProfileDetails(): void {
    this.isProfileLoading = true;
    this.profileService
      .getUserProfileDetails()
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.userProfileDetails = res;
        this.isProfileLoading = false;
      });
  }

  setActiveTab(tab: 'my-profile' | 'cdfi-profile'): void {
    this.activeTab = tab;

    if (tab === 'cdfi-profile') {
      this.imageService.GetConnectionImage();
    }
  }

  onTabChange(tab: 'my-profile' | 'cdfi-profile'): void {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { tab: tab },
      queryParamsHandling: 'merge',
    });
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      const validExtensions = ['jpg', 'jpeg', 'png', 'heic', 'heif'];
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      const fileSizeMB = file.size / (1024 * 1024);

      if (!fileExtension || !validExtensions.includes(fileExtension)) {
        this.showToastMessage(
          'Invalid file type. Only .jpg, .jpeg, .png, .heic, .heif are allowed.',
          'error',
        );
        input.value = '';
        return;
      }

      if (fileSizeMB > 10) {
        this.showToastMessage('File size exceeds 10MB limit.', 'error');
        input.value = '';
        return;
      }
      this.uploadProfileImage(file, input);
    } else {
      input.value = '';
      this.closeProfileDropdown();
    }
  }

  uploadProfileImage(file: File, input: HTMLInputElement) {
    this.imageService
      .uploadProfileImage(file)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          input.value = '';
          this.closeProfileDropdown();
        },
        error: (err) => {
          this.showToastMessage(
            err.error?.detail || 'Failed to upload profile image',
            'error',
          );
          input.value = '';
          this.closeProfileDropdown();
        },
      });
  }

  onRemoveFromDnd(): void {
    if (!this.dndEmail) return;

    this.loadingDnd = true;

    this.dndEmailService
      .disableDnd()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.dndEmail = null;
          this.loadingDnd = false;
          this.loaderDnd = false;

          this.showToastMessage(
            'Email notifications have been successfully resumed.',
            'success',
          );
        },
        error: () => {
          this.loadingDnd = false;
          this.loaderDnd = false;
        },
      });
  }

  toggleProfileDropdown() {
    this.isProfileDropdownOpen = !this.isProfileDropdownOpen;
  }

  closeProfileDropdown() {
    this.isProfileDropdownOpen = false;
  }

  onIconClick(fileInput: HTMLInputElement) {
    // If no profile image exists, directly open file picker
    if (!this.hasProfileImage) {
      fileInput.click();
    } else {
      // If image exists, show dropdown menu
      this.toggleProfileDropdown();
    }
  }

  deleteProfileImage() {
    this.showDeletePopup = true;
    this.closeProfileDropdown();
  }

  onConfirmDelete() {
    this.imageService.deleteProfileImage();
    this.showDeletePopup = false;
  }

  onCancelDelete() {
    this.showDeletePopup = false;
  }

  loadDndEmail(): void {
    this.loadingDnd = true;

    this.dndEmailService
      .getDndEmail()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.dndEmail = res;
          this.loadingDnd = false;
          this.loaderDnd = false;
        },
        error: () => {
          this.dndEmail = null;
          this.loadingDnd = false;
          this.loaderDnd = false;
        },
      });
  }

  showToastMessage(message: string, type: 'success' | 'error'): void {
    this.commonService.triggerToast({
      type: type === 'success' ? ToastType.Success : ToastType.Error,
      message: message,
    });
  }

  onEditProfile(): void {
    this.editFirstName = this.userProfileDetails?.first_name || '';
    this.editLastName = this.userProfileDetails?.last_name || '';
    this.showProfileEditPopup = true;
  }

  onFirstNameChange(): void {
    this.isFirstnameErr = false;
  }

  saveProfileDetails(): void {
    if (this.editFirstName.trim() === '') {
      this.isFirstnameErr = true;
      return;
    }
    this.isFirstnameErr = false;
    const hasChanges =
      this.editFirstName.trim() !==
        (this.userProfileDetails?.first_name || '') ||
      this.editLastName.trim() !== (this.userProfileDetails?.last_name || '');

    if (!hasChanges) {
      this.showProfileEditPopup = false;
      this.commonService.triggerToast({
        message: 'Profile details updated successfully.',
        type: ToastType.Success,
      });
      return;
    }
    const payload = {
      first_name: this.editFirstName,
      last_name: this.editLastName,
    };
    this.profileService
      .updateUserProfileDetails(payload)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.editFirstName = '';
          this.editLastName = '';
          this.showProfileEditPopup = false;
          this.getProfileDetails();
        }),
      )
      .subscribe({
        next: () => {
          const name = [payload.first_name, payload.last_name]
            .filter(Boolean)
            .join(' ');
          this.commonService.updateUserName(name);
          this.commonService.triggerToast({
            message: 'Profile details updated successfully.',
            type: ToastType.Success,
          });
        },
        error: () => {
          this.commonService.triggerToast({
            message: 'Failed to update profile details.',
            type: ToastType.Error,
          });
        },
      });
  }
}
